import { useCallback, useMemo } from 'react';
import { OfficeHoursWeekSchedule } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { CalendarView } from '../../components/calendar-view/calendar-view';
import { CalendarViewMain } from '../../components/calendar-view/calendar-view-main';
import { CalendarViewOffices, OfficeCalendarView } from '../../components/calendar-view/types';
import { EmptyCalendarMessage } from '../../components/empty-calendar-message';
import { EventCardComponent } from '../../components/schedule-calendar-components/event-card';
import { useGetCalendarV2QueriesData } from '../../hooks/use-get-calendar-v2-queries-data';
import { useGetOfficeHoursEvent } from '../../hooks/use-get-office-hours-events';
import { useGetProvidersDetailsForCalendar } from '../../hooks/use-get-providers-details-for-calendar';
import { useCalendarLocationIds } from '../../stores/calendar-location.store';
import { useCalendarStore } from '../../stores/calendar.store';
import { CalendarAutoScroll } from '../CalendarV3/components/calendar-auto-scroll';
import { getOfficeWorkingHours } from './helpers';
import { CalendarEventType } from './types';
import { isUUID } from './utils';

export type CalendarTimeLineViewV2Props = Pick<
  ReturnType<typeof useGetCalendarV2QueriesData>,
  | 'filteredAppointments'
  | 'filteredProviderList'
  | 'providerExceptions'
  | 'providersOfficeHours'
  | 'officeHours'
  | 'officeExceptions'
  | 'isLoading'
>;

export const CalendarTimeLineViewV2 = ({
  filteredAppointments,
  filteredProviderList,
  providerExceptions,
  providersOfficeHours,
  officeHours,
  officeExceptions,
  isLoading,
}: CalendarTimeLineViewV2Props) => {
  const { getScopeName } = useAppScopeStore();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const { calendarLocationIds } = useCalendarLocationIds();

  const { getOfficeHoursEvents } = useGetOfficeHoursEvent();

  const { getProvidersForCalendarObject } = useGetProvidersDetailsForCalendar({
    appointments: filteredAppointments,
    providersList: filteredProviderList,
    providersExceptions: providerExceptions,
    providersOfficeHours: providersOfficeHours,
  });

  const getOfficeHoursEventsData = useCallback(
    (locationId: string) => {
      const officeHoursData =
        officeHours?.find((office) => office.locationId === locationId)?.officeHours ?? ([] as OfficeHoursWeekSchedule);

      const officeHoursEvents = getOfficeHoursEvents({
        calendarDateValue: calendarDate,
        officeHoursData: officeHoursData,
        officeExceptions: officeExceptions,
        locationId: locationId,
      });
      return officeHoursEvents;
    },
    [officeHours, getOfficeHoursEvents, calendarDate, officeExceptions]
  );

  const getOfficeCalendarViewObject = useCallback(
    (locationId: string): OfficeCalendarView => {
      const officeHoursEvents = getOfficeHoursEventsData(locationId);
      return {
        officeName: getScopeName(locationId),
        officeTimezone: '', // Note: timezone is not used in V2
        officeBreaks: officeHoursEvents, // this is used for V2 only
        officeExceptions: officeHoursEvents, // This is used by common AllDayEvents component, so we are keeping it here
        columnEntities: getProvidersForCalendarObject(locationId),
        workingHours: getOfficeWorkingHours({ officeHours, calendarDate, locationId }),
        locationId: locationId,
        calendarDateValue: calendarDate,
      };
    },
    [getOfficeHoursEventsData, getScopeName, getProvidersForCalendarObject, officeHours, calendarDate]
  );

  const calendarObject = useMemo<CalendarViewOffices>(() => {
    if (!!calendarLocationIds?.length) {
      return calendarLocationIds.map((locationId) => {
        return getOfficeCalendarViewObject(locationId ?? '');
      });
    }
    return [getOfficeCalendarViewObject(calendarLocationIds[0])];
  }, [calendarLocationIds, getOfficeCalendarViewObject]);

  const handleConfiguredCalendarEvent = (configuredCalendarEvent: CalendarEventType) => {
    const isProviderEvent = configuredCalendarEvent.providerId && isUUID(configuredCalendarEvent.providerId);
    const isOfficeEvent = configuredCalendarEvent.locationId && !configuredCalendarEvent.providerId;
    if (isOfficeEvent || isProviderEvent) {
      setConfiguredCalendarEvent(configuredCalendarEvent);
    }
  };

  return (
    <CalendarView
      data={calendarObject}
      onEventCreate={(startHour, endHour, providerName, providerId, isValid, locationId, calendarDateValue) => {
        handleConfiguredCalendarEvent({
          startHour,
          endHour,
          providerName,
          providerId,
          isValid,
          locationId,
          calendarDateValue,
        });
      }}
      EventCardComponent={EventCardComponent}
      EmptyCalendarComponent={!isLoading ? EmptyCalendarMessage : undefined}
      shouldAllowEventCreation={!isIntegratedOffice}
      calendarDate={calendarDate}
    >
      <CalendarAutoScroll calendarObject={calendarObject} isLoadingCalendarEvents={isLoading} />
      <CalendarViewMain />
    </CalendarView>
  );
};
